import React, { createContext, ReactNode, useContext, useState } from "react";
import { Header, ModelInfo } from "build-ts/metadata";
import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import { useDisclosure } from "@chakra-ui/react";
import { getAllKGColumns, getDataModelHeaders, getModelById } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import { deleteMetric, getMetricById, listMetrics, getBenchmarkById, deleteBenchmark, deleteKgColumn } from "services/MetricService";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";

interface ContextType {
  metricList: Header[];
  modelList: Header[];
  selectedMetric: Header | null;
  setSelectedMetric: React.Dispatch<React.SetStateAction<Header | null>>;
  editMetricDefinition: boolean;
  setEditMetricDefinition: (editMetricDefinition: boolean) => void;
  editMKG: boolean;
  setEditMKG: (editMKG: boolean) => void;
  viewMKG: boolean;
  setViewMKG: (viewMKG: boolean) => void;
  metricInfo: CreateOrUpdateMetrics_Request | null;
  setMetricInfo: React.Dispatch<React.SetStateAction<CreateOrUpdateMetrics_Request | null>>;
  modelInfo: ModelInfo | null;
  setModelInfo: React.Dispatch<React.SetStateAction<ModelInfo | null>>;
  
  // Separate modal states for each component type
  metricModalState: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  benchmarkModalState: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  kgColumnModalState: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  
  
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  closeModal: () => void;
  closeBenchmarkModal: () => void;
  closeKgColumnModal: () => void;
  
  deleteMetricDialog: boolean;
  metricId: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  // Single dependencies state
  dependencies: Header[];
  setDependencies: (dependencies: Header[]) => void;
  showDependenciesModal: boolean;
  setShowDependenciesModal: (show: boolean) => void;
  
  fetchAllMetrics: () => Promise<void>;
  handleEditMetricDefinitionModal: (metricHeader: Header) => void;
  updateMetric: (metricHeader: Header, modelId: string) => Promise<void>;
  handleEditMKGModal: (metricHeader: Header, modelId: string) => void;
  handleDeleteMetricDialog: (id: string) => void;
  handleDeleteMetric: (id: string) => void;
  deleteMetricById: (id: string) => Promise<void>;
  closeDeleteMetricDialog: () => void;
  closeDeleteMetricModal: () => Promise<void>;
  handleViewMKGModal: (metricHeader: Header) => void;

  // kg column
  selectedKgColumn: Header | null;
  setSelectedKgColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  editColumn: boolean;
  setEditColumn: (editColumn: boolean) => void;
  handleEditColumnModal: (columnHeader: Header) => void;
  kgColumns: Header[] | null;
  fetchKgColumns: () => Promise<Header[]>;
  deleteKgColumnFlag: boolean;
  deleteKgColumnDialog: boolean;
  setDeleteKgColumnFlag: (deleteKgColumnFlag: boolean) => void;
  setDeleteKgColumnDialog: (deleteKgColumnDialog: boolean) => void;
  deletedKgColumnId: string;
  setDeletedKgColumnId: (id: string) => void;
  handleDeleteKgColumnDialog: (id: string) => void;
  handleDeleteKgColumn: (id: string) => void;
  deleteKgColumnById: (id: string) => Promise<void>;
  closeDeleteKgColumnDialog: () => void;
  closeDeleteKgColumnModal: () => void;

  // benchmark
  selectedBenchmark: Header | null;
  setSelectedBenchmark: React.Dispatch<React.SetStateAction<Header | null>>;
  selectedBenchmarkId: BenchmarkEntityProto | null;
  setSelectedBenchmarkId: (benchmark: BenchmarkEntityProto | null) => void;
  editBenchmark: boolean;
  setEditBenchmark: (editBenchmark: boolean) => void;
  deleteBenchmarkFlag: boolean;
  deleteBenchmarkDialog: boolean;
  setDeleteBenchmarkFlag: (deleteBenchmarkFlag: boolean) => void;
  setDeleteBenchmarkDialog: (deleteBenchmarkDialog: boolean) => void;
  deletedBenchmarkId: string;
  setDeletedBenchmarkId: (id: string) => void;
  handleEditBenchmark: (benchmark: Header) => void;
  fetchBenchmarkById: (id: string) => Promise<void>;
  handleDeleteBenchmarkDialog: (id: string) => void;
  handleDeleteBenchmark: (id: string) => void;
  deleteBenchmarkById: (id: string) => Promise<void>;
  closeDeleteBenchmarkDialog: () => void;
  closeDeleteBenchmarkModal: () => void;
}

const ApiContext = createContext<ContextType | undefined>(undefined);

export const useConfigurationContext = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
};

export const ConfigurationContextProvider: React.FC<{
  children: ReactNode;
}> = ({children}) => {
  // Separate modal states for each component type
  const metricModalState = useDisclosure();
  const benchmarkModalState = useDisclosure();
  const kgColumnModalState = useDisclosure();
  
  // Legacy support - use metric modal state for backward compatibility
  const { isOpen, onOpen, onClose } = metricModalState;
  
  const [metricList, setMetricList] = useState<Header[]>([]);
  const [modelList, setModelList] = useState<Header[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<Header | null>(null);
  const [editMetricDefinition, setEditMetricDefinition] = useState<boolean>(false);
  const [editMKG, setEditMKG] = useState<boolean>(false);
  const [viewMKG, setViewMKG] = useState<boolean>(false);
  const [metricInfo, setMetricInfo] = useState<CreateOrUpdateMetrics_Request | null>(null);
  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null);
  const [deleteMetricDialog, setDeleteMetricDialog] = useState<boolean>(false);
  const [metricId, setMetricId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  // Single dependencies state to replace all separate dependency states
  const [dependencies, setDependencies] = useState<Header[]>([]);
  const [showDependenciesModal, setShowDependenciesModal] = useState<boolean>(false);

  // state for kg column
  const [selectedKgColumn, setSelectedKgColumn] = useState<Header | null>(null);
  const [editColumn, setEditColumn] = useState<boolean>(false);
  const [kgColumns, setKgColumns] = useState<Header[] | null>(null);
  const [deleteKgColumnFlag, setDeleteKgColumnFlag] = useState<boolean>(false);
  const [deleteKgColumnDialog, setDeleteKgColumnDialog] = useState<boolean>(false);
  const [deletedKgColumnId, setDeletedKgColumnId] = useState<string>("");

  // state for benchmark
  const [selectedBenchmark, setSelectedBenchmark] = useState<Header | null>(null);
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useState<BenchmarkEntityProto | null>(null);
  const [editBenchmark, setEditBenchmark] = useState<boolean>(false);
  const [deleteBenchmarkFlag, setDeleteBenchmarkFlag] = useState<boolean>(false);
  const [deleteBenchmarkDialog, setDeleteBenchmarkDialog] = useState<boolean>(false);
  const [deletedBenchmarkId, setDeletedBenchmarkId] = useState<string>("");

  // fetch all kg columns
  const fetchKgColumns = async (): Promise<Header[]> => {
    try {
        const kgColumns = await getAllKGColumns();
        if(kgColumns) {
          setKgColumns(kgColumns);
          return kgColumns;
        }
        return [];
    } catch (error) {
      showToast("Error fetching KG columns", "", "error");
      return [];
    }
  };

  const closeModal = () => {
    // Reset only metric-related modal flags when closing
    setEditMetricDefinition(false);
    setEditMKG(false);
    setViewMKG(false);
    setSelectedMetric(null);
    metricModalState.onClose();
  };

  // Close benchmark modal and reset related states
  const closeBenchmarkModal = () => {
    setEditBenchmark(false);
    setSelectedBenchmark(null);
    setSelectedBenchmarkId(null);
    benchmarkModalState.onClose();
  };

  // Close KG column modal and reset related states
  const closeKgColumnModal = () => {
    setEditColumn(false);
    setSelectedKgColumn(null);
    kgColumnModalState.onClose();
  };

  // handle edit kg-column modal
  const handleEditColumnModal = (columnHeader: Header) => {
    setEditColumn(true);
    setSelectedKgColumn(columnHeader);
    kgColumnModalState.onOpen();
  };

  // handle edit benchmark modal
  const handleEditBenchmark = (benchmark: Header) => {
    setEditBenchmark(true);
    setSelectedBenchmark(benchmark);
    fetchBenchmarkById(benchmark?.id || "");
    benchmarkModalState.onOpen(); // Open the benchmark modal
  };

  // fetch benchmark by id
  const fetchBenchmarkById = async (id: string) => {
    setLoading(true);
    try {
      const data = await getBenchmarkById(id, true);
      if (data) {
        setSelectedBenchmarkId(data);
      }
    } catch (error) {
      showToast("Error fetching benchmark details", "", "error");
    } finally {
      setLoading(false);
    }
  };

  // fetch all metrics
  const fetchAllMetrics = async () => {
    try{
      // First, fetch all models
      const models = await getDataModelHeaders(false);
      if(!models || models.length === 0) {
        return;
      }
      if(models){
        setModelList(models);
      }

      // fetch all metrics
      const metrics = await listMetrics("");
      setMetricList(metrics);
    } catch (error) {
      showToast("Error fetching metrics", "", "error");
    }
  };

  // handle edit metric definition modal
  const handleEditMetricDefinitionModal = (metricHeader: Header) => {
    setSelectedMetric(metricHeader);
    setEditMKG(false);
    setViewMKG(false);
    setEditMetricDefinition(true);
    metricModalState.onOpen();
  };

  const updateMetric = async (metricHeader: Header, modelId: string) => {
    try {
      const data = await getModelById(modelId);
      if(data) {
        setModelInfo(data);
      }
    } catch (error) {
      showToast("Error fetching data model", "", "error");
    }
    try {
      const data = await getMetricById(metricHeader.id!, true);
      const formData: CreateOrUpdateMetrics_Request = {
        model_id: String(modelId),
        metrics: [
          {
            header: {
              ...data?.header,
            },
            definition: {
              observation_columns: data?.definition?.observation_columns ?? [],
              filters: data?.definition?.filters ?? [],
            },
            edges: data?.edges ?? [],
            format: data?.format,
            type: data?.type,
            polarity: data?.polarity,
            material_column_header: data?.material_column_header
          },
        ],
        update_edges: true,
      };
      setMetricInfo(formData);
    } catch (error) {
      showToast("Error updating MKG.", "", "error");
    }
  };

  // handle edit mkg modal
  const handleEditMKGModal = (metricHeader: Header, modelId: string) => {
    updateMetric(metricHeader, modelId);
    setSelectedMetric(metricHeader);
    setEditMetricDefinition(false);
    setViewMKG(false);
    setEditMKG(true);
    metricModalState.onOpen();
  };

  // handle view graph modal
  const handleViewMKGModal = (metricHeader: Header) => {
    setSelectedMetric(metricHeader);
    setEditMetricDefinition(false);
    setEditMKG(false);
    setViewMKG(true);
    metricModalState.onOpen();
  };

  // handle delete metric alert-dialog box
  const handleDeleteMetricDialog = (id: string) => {
    setMetricId(id);
    setDeleteMetricDialog(true);
  };

  // handle delete metric
  const deleteMetricById  = async (id: string) => {
    const response: any = await deleteMetric(id);
    if(response) {
      setDependencies(response);
      setShowDependenciesModal(true);
    } else {
      showToast("Metric deleted successfully", "", "success");
      window.location.reload();
    }
  };

  // handler function for delete metric
  const handleDeleteMetric = (id: string) => {
    closeDeleteMetricDialog();
    deleteMetricById(id);
  };

  // close delete metric alert-dialog box
  const closeDeleteMetricDialog = () => {
    setDeleteMetricDialog(false);
  };
  
  // close delete metric modal
  const closeDeleteMetricModal = async () => {
    setShowDependenciesModal(false);
    setDependencies([]);
  };

  // handle delete benchmark alert-dialog box
  const handleDeleteBenchmarkDialog = (id: string) => {
    setDeletedBenchmarkId(id);
    setDeleteBenchmarkDialog(true);
  };

  // close delete benchmark alert-dialog box
  const closeDeleteBenchmarkDialog = () => {
    setDeleteBenchmarkDialog(false);
  };

  // handle delete benchmark
  const deleteBenchmarkById = async (id: string) => {
    const response: any = await deleteBenchmark(deletedBenchmarkId);
    if(response) {
      setDependencies(response);
      setShowDependenciesModal(true);
    } else {
      showToast("Benchmark deleted successfully", "", "success");
      window.location.reload();
    }
  };

  // handler function for delete benchmark
  const handleDeleteBenchmark = (id: string) => {
    closeDeleteBenchmarkDialog();
    deleteBenchmarkById(id);
  };

  // close delete benchmark modal
  const closeDeleteBenchmarkModal = () => {
    setShowDependenciesModal(false);
    setDependencies([]);
  };

  // handle delete kg-column alert-dialog box
  const handleDeleteKgColumnDialog = (id: string) => {
    setDeletedKgColumnId(id);
    setDeleteKgColumnDialog(true);
  };

  // close delete kg-column alert-dialog box
  const closeDeleteKgColumnDialog = () => {
    setDeleteKgColumnDialog(false);
  };

  // handle delete kg-column
  const deleteKgColumnById = async (id: string) => {
    const response: any = await deleteKgColumn(id);
    if(response) {
      setDependencies(response);
      setShowDependenciesModal(true);
    } else {
      // Successful deletion (204 status) - response is null
      showToast("KG column deleted successfully", "", "success");
      window.location.reload();
    }
  };

  // handler function for delete kg-column
  const handleDeleteKgColumn = (id: string) => {
    setDeleteKgColumnDialog(false);
    deleteKgColumnById(id);
  };

  // close delete kg-column modal
  const closeDeleteKgColumnModal = () => {
    setShowDependenciesModal(false);
    setDependencies([]);
  };
  
  return (
    <ApiContext.Provider 
      value={{
        metricList,
        modelList,
        selectedMetric,
        setSelectedMetric,
        editMetricDefinition,
        setEditMetricDefinition,
        editMKG,
        setEditMKG,
        viewMKG,
        setViewMKG,
        metricInfo,
        setMetricInfo,
        modelInfo,
        setModelInfo,
        
        // Separate modal states for each component type
        metricModalState,
        benchmarkModalState,
        kgColumnModalState,
        
        // Legacy support (deprecated)
        isOpen,
        onOpen,
        onClose,
        closeModal,
        closeBenchmarkModal,
        closeKgColumnModal,
        
        deleteMetricDialog,
        metricId,
        loading,
        setLoading,
        // Single dependencies state
        dependencies,
        setDependencies,
        showDependenciesModal,
        setShowDependenciesModal,
        
        fetchAllMetrics,
        handleEditMetricDefinitionModal,
        updateMetric,
        handleEditMKGModal,
        handleDeleteMetricDialog,
        handleDeleteMetric,
        deleteMetricById,
        closeDeleteMetricDialog,
        closeDeleteMetricModal,
        //deleteAnalysis,
        handleViewMKGModal,

        // kg column
        selectedKgColumn,
        setSelectedKgColumn,
        editColumn,
        setEditColumn,
        handleEditColumnModal,
        kgColumns,
        fetchKgColumns,
        deleteKgColumnFlag,
        deleteKgColumnDialog,
        setDeleteKgColumnFlag,
        setDeleteKgColumnDialog,
        deletedKgColumnId,
        setDeletedKgColumnId,
        handleDeleteKgColumnDialog,
        handleDeleteKgColumn,
        deleteKgColumnById,
        closeDeleteKgColumnDialog,
        closeDeleteKgColumnModal,

        // benchmark
        selectedBenchmark,
        setSelectedBenchmark,
        selectedBenchmarkId,
        setSelectedBenchmarkId,
        editBenchmark,
        setEditBenchmark,
        deleteBenchmarkDialog,
        deleteBenchmarkFlag,
        setDeleteBenchmarkFlag,
        setDeleteBenchmarkDialog,
        deletedBenchmarkId,
        setDeletedBenchmarkId,
        handleEditBenchmark,
        fetchBenchmarkById,
        handleDeleteBenchmarkDialog,
        handleDeleteBenchmark,
        deleteBenchmarkById,
        closeDeleteBenchmarkDialog,
        closeDeleteBenchmarkModal
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};